import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCustomDialog } from './CustomDialog';
import { getActivityNotes, deleteActivityLog, deletePatientCase, createTask } from '../firebase';
import { User, ActivityNote, Task } from '../types';
import { formatDate } from '../utils';
import { getApiUrl } from '../config';
import { generatePatientPDF } from '../utils/pdfGenerator';
import { AIChatButton } from './AIChatButton';
import IntakeFormsSection from './PatientDetail/IntakeFormsSection';
import DocumentsSection from './PatientDetail/DocumentsSection';
import PatientInformationSection from './PatientDetail/PatientInformationSection';
import { MeetingModal } from './PatientDetail/MeetingModal';
import { MilestoneModals } from './PatientDetail/MilestoneModals';
import { ProgressSection } from './PatientDetail/ProgressSection';
import { AITabSummary } from './PatientDetail/AITabSummary';
import { Sidebar, MobileHeader } from './dashboard';
import { canViewPatient, canEditMeeting, canCommentOnMeeting, canAddMilestone, canEditMilestone, canDeleteMilestone, canManageDocuments, isSuperAdmin, canGenerateReports } from '../utils/roleUtils';
import { AssignedUserAvatar } from './PatientComponents';
import { updatePatientAssignedAdmins } from '../firebase';

// Patient Detail Page Component
export function PatientDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showAlert, showConfirm, DialogComponent } = useCustomDialog();

  // Set document direction based on language
  useEffect(() => {
    document.documentElement.dir = i18n.language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Helper function to translate stored values to display text
  const translateValue = (field: string, value: string) => {
    if (!value || value === '') return 'N/A';
    
    switch (field) {
      case 'gender':
        return value === 'male' ? t('intakeForm.male') : t('intakeForm.female');
      case 'marital_status':
        return value === 'single' ? t('patientDetail.single') : t('patientDetail.married');
      case 'education':
        switch (value) {
          case 'elementary': return t('patientDetail.elementarySchool');
          case 'high_school': return t('patientDetail.highSchool');
          case 'college': return t('patientDetail.college');
          case 'university': return t('patientDetail.university');
          case 'graduate': return t('patientDetail.graduateDegree');
          case 'other': return t('patientDetail.other');
          default: return value;
        }
      default:
        return value;
    }
  };
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Auto-close mobile menu when window resizes to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);
  
  // Auto-close mobile menu when clicking outside (on mobile)
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const sidebar = document.querySelector('.sidebar');
      const overlay = document.querySelector('.mobile-menu-overlay');
      
      // Check if click is outside sidebar and not on overlay
      if (sidebar && !sidebar.contains(target) && window.innerWidth <= 768) {
        // If clicking on overlay or main content, close menu
        if (overlay?.contains(target) || target.closest('.main-content')) {
          setIsMobileMenuOpen(false);
        }
      }
    };
    
    // Add listener with a small delay to avoid immediate closure
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobileMenuOpen]);
  const [patientPII, setPatientPII] = useState<any>(null);
  const [activities, setActivities] = useState<ActivityNote[]>([]);
  const [showMenu, setShowMenu] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [intakeStatus, setIntakeStatus] = useState<{ rights?: boolean; emotional?: boolean; professional?: boolean }>({});
  const [activeDetailTab, setActiveDetailTab] = useState<'documents' | 'activity' | 'patient-info' | 'notes' | 'progress' | 'tasks'>('progress');
  const [isEditing, setIsEditing] = useState(false);
  const [editedPatientData, setEditedPatientData] = useState<any>(null);
  const [milestones, setMilestones] = useState<any[]>([]);
  const [showAddMilestoneModal, setShowAddMilestoneModal] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    dueDate: new Date().toISOString().split('T')[0]
  });
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [showEditMilestoneModal, setShowEditMilestoneModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<any>(null);
  const [newMilestone, setNewMilestone] = useState({ 
    title: '', 
    description: '', 
    progress: 0, 
    targetDate: '', 
    status: 'new',
    axis: 'emotional', // ציר - Emotional/Occupational/Rights
    successMetric: '', // מדד הצלחה - success criteria
    resources: '', // משאב רלוונטי - relevant resources
    barriers: '', // חסם פוטנציאלי - potential barriers (legacy)
    internalBarriers: '', // חסמים פנימיים - internal barriers
    externalBarriers: '', // חסמים חיצוניים - external barriers
    notes: '', // הערות/מעקב שיחה - notes/conversation tracking
    therapistNotes: '' // הערת מטפל - therapist notes
  });
  const [showAddMeetingModal, setShowAddMeetingModal] = useState(false);
  const [newMeeting, setNewMeeting] = useState({ description: '', date: '', notes: '' });
  const [editingMeeting, setEditingMeeting] = useState<any>(null);
  const [meetingFilter, setMeetingFilter] = useState<'active' | 'archived'>('active');
  const [commentingMeetingId, setCommentingMeetingId] = useState<string | null>(null);
  const [meetingComment, setMeetingComment] = useState('');
  const [draggedMilestone, setDraggedMilestone] = useState<any>(null);

  const [selectedMilestones, setSelectedMilestones] = useState<Set<string>>(new Set());
  const [patientMetadata, setPatientMetadata] = useState<any>(null); // Store patient Firebase metadata for permissions
  const [userPrograms, setUserPrograms] = useState<string[]>([]); // User's managed programs
  const [userGroups, setUserGroups] = useState<string[]>([]); // User's managed groups

  // Function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Function to open meeting modal with today's date pre-selected
  const openAddMeetingModal = () => {
    setEditingMeeting(null);
    setNewMeeting({ description: '', date: getTodayDate(), notes: '' });
    setShowAddMeetingModal(true);
  };

  
  // Functions for handling milestone selection and completion
  const handleMilestoneSelect = async (milestoneId: string, checked: boolean) => {
    // For social workers: one-way checkbox - can only check, cannot uncheck
    const isSocialWorker = user?.organizationRole === 'social_worker';
    if (isSocialWorker && !checked) {
      // Social workers cannot uncheck milestones
      showAlert(t('patientDetail.cannotUncheckMilestone') || 'You cannot uncheck milestones. Once marked as achieved, they remain achieved.');
      return;
    }

    // Update local state immediately for responsive UI
    const newSelected = new Set(selectedMilestones);
    if (checked) {
      newSelected.add(milestoneId);
      // Mark milestone as completed
      setMilestones(milestones.map(milestone => 
        milestone.id === milestoneId 
          ? { ...milestone, status: 'achieved', progress: 100 } 
          : milestone
      ));
    } else {
      newSelected.delete(milestoneId);
      // Mark milestone as in progress
      setMilestones(milestones.map(milestone => 
        milestone.id === milestoneId 
          ? { ...milestone, status: 'in_progress', progress: milestone.progress || 0 } 
          : milestone
      ));
    }
    setSelectedMilestones(newSelected);

    try {
      // Update in Firebase
      const { db } = await import('../firebase');
      const { doc, updateDoc } = await import('firebase/firestore');
      
      const milestoneRef = doc(db, 'milestones', milestoneId);
      
      if (checked) {
        // Mark as completed
        await updateDoc(milestoneRef, {
          status: 'achieved',
          progress: 100,
          updatedAt: new Date().toISOString()
        });
        console.log('Milestone marked as completed:', milestoneId);
      } else {
        // Mark as in progress
        const milestone = milestones.find(m => m.id === milestoneId);
        const originalProgress = milestone?.progress || 0;
        await updateDoc(milestoneRef, {
          status: 'in_progress',
          progress: originalProgress > 100 ? 0 : originalProgress, // Reset if it was 100%
          updatedAt: new Date().toISOString()
        });
        console.log('Milestone marked as in progress:', milestoneId);
      }
    } catch (error) {
      console.error('Error updating milestone completion status:', error);
      // Revert local state on error
      if (checked) {
        setMilestones(milestones.map(milestone => 
          milestone.id === milestoneId 
            ? { ...milestone, status: milestone.status, progress: milestone.progress } 
            : milestone
        ));
        newSelected.delete(milestoneId);
      } else {
        setMilestones(milestones.map(milestone => 
          milestone.id === milestoneId 
            ? { ...milestone, status: milestone.status, progress: milestone.progress } 
            : milestone
        ));
        newSelected.add(milestoneId);
      }
      setSelectedMilestones(newSelected);
    }
  };

  // Function to open meeting modal for editing
  const openEditMeetingModal = (activity: any) => {
    // Check if user has permission to edit this meeting
    if (!canEditMeeting(user, activity)) {
      showAlert(t('patientDetail.cannotEditMeeting') || 'You do not have permission to edit this meeting');
      return;
    }
    
    setEditingMeeting(activity);
    setNewMeeting({
      description: activity.meetingDescription || activity.note.replace(/^Meeting: /, '').split(' - ')[0],
      date: activity.meetingDate || '',
      notes: activity.meetingNotes || ''
    });
    setShowAddMeetingModal(true);
  };

  useEffect(() => {
    console.log('Loading user from localStorage...');
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        console.log('User loaded from localStorage:', userData);
        setUser(userData);
      } catch (err) {
        console.log('Error parsing user from localStorage:', err);
        localStorage.removeItem('user');
      }
    } else {
      console.log('No user found in localStorage');
    }
    setIsLoading(false);
  }, []);

  // Load intake completion flags
  useEffect(() => {
    const loadIntakes = async () => {
      if (!caseId) return;
      try {
        const { db } = await import('../firebase');
        const { doc, getDoc } = await import('firebase/firestore');
        const rightsRef = doc(db, 'patients', String(caseId), 'intakes', 'rights');
        const emotionalRef = doc(db, 'patients', String(caseId), 'intakes', 'emotional');
        const professionalRef = doc(db, 'patients', String(caseId), 'intakes', 'professional');
        const [r, e, p] = await Promise.all([getDoc(rightsRef), getDoc(emotionalRef), getDoc(professionalRef)]);
        setIntakeStatus({
          rights: r.exists() && !!r.data()?.completed,
          emotional: e.exists() && !!e.data()?.completed,
          professional: p.exists() && !!p.data()?.completed
        });
      } catch (err) {
        console.log('Failed to load intake flags', err);
      }
    };
    loadIntakes();
  }, [caseId]);

  // Function to handle tab change and close mobile menu
  const handleTabChange = (tab: string) => {
    setIsMobileMenuOpen(false);
    
    // Navigate to dashboard with the appropriate tab
    if (tab === 'dashboard') {
      navigate('/dashboard');
    } else {
      // For all other tabs, navigate to dashboard with tab parameter
      navigate(`/dashboard?tab=${tab}`);
    }
  };

  useEffect(() => {
    if (caseId && user) {
      loadPatientData();
      loadMilestones();
      loadTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, user]);

  // Check URL hash on mount and when location changes to set active tab
  useEffect(() => {
    const checkHash = () => {
      const hash = window.location.hash;
      if (hash === '#intake' || hash === '#notes') {
        setActiveDetailTab('notes');
      }
    };
    
    // Check on mount
    checkHash();
    
    // Listen for hash changes
    window.addEventListener('hashchange', checkHash);
    
    return () => {
      window.removeEventListener('hashchange', checkHash);
    };
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenu) {
        const target = event.target as Element;
        if (!target.closest('.patient-actions-menu')) {
          setShowMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Debug tab switching
  useEffect(() => {
    console.log('Active tab changed to:', activeDetailTab);
  }, [activeDetailTab]);

  const loadPatientData = async () => {
    if (!caseId || !user) return;
    
    try {
      // Load patient metadata from Firebase to check permissions
      const { db } = await import('../firebase');
      const { doc, getDoc, collection, getDocs } = await import('firebase/firestore');
      const patientRef = doc(db, 'patients', caseId);
      const patientDoc = await getDoc(patientRef);
      
      if (!patientDoc.exists()) {
        // Show confirmation dialog with delete option
        showConfirm(
          `${t('patientDetail.patientNotFound') || 'Patient not found'}: ${caseId}. ${t('patientDetail.deleteCaseQuestion') || 'Would you like to delete this case?'}`,
          async () => {
            if (!caseId || !user) return;
            try {
              const result = await deletePatientCase(caseId, user.id);
              if (result.success) {
                showAlert(t('patientDetail.caseDeleted') || `Case ${caseId} has been deleted successfully.`);
                navigate('/dashboard?tab=projects');
              } else {
                showAlert(t('patientDetail.deleteFailed') || `Failed to delete case ${caseId}. Please try again.`);
              }
            } catch (error) {
              console.error('Error deleting case:', error);
              showAlert(t('patientDetail.deleteFailed') || `Failed to delete case ${caseId}. Please try again.`);
            }
          },
          t('patientDetail.patientNotFound') || 'Patient Not Found',
          t('patientDetail.deleteCase') || 'Delete Case',
          t('dialog.cancel') || 'Cancel'
        );
        return;
      }
      
      const patient = { id: patientDoc.id, ...patientDoc.data() } as any;
      
      console.log('=== PERMISSION CHECK DEBUG ===');
      console.log('Current user:', user);
      console.log('User ID:', user.id);
      console.log('User role:', user.role);
      console.log('User organizationRole:', user.organizationRole);
      console.log('Patient data:', patient);
      console.log('Patient createdBy:', patient.createdBy);
      console.log('Patient assignedAdmins:', patient.assignedAdmins);
      
      // Load user's programs and groups for permission check
      const programsSnapshot = await getDocs(collection(db, 'programs'));
      const programs = programsSnapshot.docs
        .filter(docSnap => {
          const data = docSnap.data();
          return Array.isArray(data.managers) && data.managers.includes(user.id);
        })
        .map(docSnap => docSnap.id);
      
      const groupsSnapshot = await getDocs(collection(db, 'groups'));
      const groups = groupsSnapshot.docs
        .filter(docSnap => {
          const data = docSnap.data();
          return Array.isArray(data.managers) && data.managers.includes(user.id);
        })
        .map(docSnap => docSnap.id);
      
      console.log('User programs:', programs);
      console.log('User groups:', groups);
      
      // Check if user has permission to view this patient
      const canView = canViewPatient(user, patient, programs, groups);
      
      console.log('Can view patient:', canView);
      console.log('=== END PERMISSION CHECK ===');
      
      if (!canView) {
        showAlert(t('patientDetail.noPermission') || 'You do not have permission to view this patient');
        navigate('/dashboard?tab=projects');
        return;
      }
      
      // Store patient metadata and user permissions for later use
      setPatientMetadata(patient);
      setUserPrograms(programs);
      setUserGroups(groups);
      
      // Load PII data from PostgreSQL via fileserver
      const piiResponse = await fetch(getApiUrl(`/api/patients/${caseId}`));
      if (piiResponse.ok) {
        const piiData = await piiResponse.json();
        setPatientPII(piiData.patient);
        
        // Load image URLs from PostgreSQL
        if (piiData.patient.image_urls) {
          try {
            const imageUrls = JSON.parse(piiData.patient.image_urls);
            setUploadedFiles(imageUrls);
          } catch (error) {
            console.error('Error parsing image URLs:', error);
            setUploadedFiles([]);
          }
        } else {
          setUploadedFiles([]);
        }
      } else if (piiResponse.status === 404) {
        console.log('Patient PII data not found');
      } else {
        console.log('Failed to load patient PII data');
      }

      // Load non-PII data from Firebase (already loaded above, reuse variables)
      if (patientDoc.exists()) {
        const firebaseData = patientDoc.data();
        // Merge Firebase data with PII data
        setPatientPII((prev: any) => ({
          ...prev,
          gender: firebaseData.gender || '',
          maritalStatus: firebaseData.maritalStatus || '',
          marital_status: firebaseData.maritalStatus || '', // Keep both for compatibility
          education: firebaseData.education || '',
          notes: firebaseData.notes || '',
          strengths: firebaseData.strengths || '',
          obstacles: firebaseData.obstacles || ''
        }));
      }

      // Load activity notes from Firebase and filter for meetings/events only
      const activitiesData = await getActivityNotes(caseId);
      // Filter to show only meetings, events, and appointments
      const filteredActivities = activitiesData.filter(activity => {
        const action = activity.action?.toLowerCase() || '';
        const note = activity.note?.toLowerCase() || '';
        return action.includes('event') || 
               action.includes('meeting') || 
               action.includes('appointment') ||
               note.includes('event') || 
               note.includes('meeting') || 
               note.includes('appointment');
      });
      setActivities(filteredActivities);
    } catch (err: any) {
      console.error('Error loading patient data:', err);
        console.log('Failed to load patient data');
    }
  };

  

  const handleDeleteFile = async (fileUrl: string, index: number) => {
    if (!caseId) return;
    
    try {
      // Remove file from the uploaded files list
      const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
      setUploadedFiles(updatedFiles);
      
      // Update the database with the new file list (remove from database only)
      const updateResponse = await fetch(getApiUrl(`/api/patients/${caseId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_urls: JSON.stringify(updatedFiles)
        })
      });

      if (updateResponse.ok) {
        console.log('File removed from database successfully');
        // Add activity note for the removal
        if (user) {
          const { addActivityNote } = await import('../firebase');
          await addActivityNote(caseId, `Document removed from patient record: ${fileUrl.split('/').pop()}`, 'removed', user.email);
        }
      } else {
        console.error('Failed to update database after file removal');
        // Revert the UI change if database update failed
        setUploadedFiles(uploadedFiles);
        console.log('Failed to remove file from database');
      }
    } catch (error) {
      console.error('Error removing file:', error);
      // Revert the UI change if there was an error
      setUploadedFiles(uploadedFiles);
      console.log('Failed to remove file');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    console.log('Starting file upload process...');
    setIsUploading(true);
    const newUploadedFiles: string[] = [];

    try {
      for (const file of Array.from(files)) {
        console.log(`Uploading file: ${file.name}, size: ${file.size}`);
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(getApiUrl('/upload'), {
          method: 'POST',
          body: formData,
        });

        console.log(`Upload response status: ${response.status}`);
        
        if (response.ok) {
          const result = await response.json();
          newUploadedFiles.push(result.file_url);
          console.log(`File uploaded successfully: ${result.file_url}`);
        } else {
          const errorText = await response.text();
          console.error(`Failed to upload file: ${file.name}, Status: ${response.status}, Error: ${errorText}`);
        }
      }

      // Save image URLs to PostgreSQL instead of local state
      if (newUploadedFiles.length > 0 && caseId) {
        const allImageUrls = [...uploadedFiles, ...newUploadedFiles];
        
        // Update patient data with image URLs
        const updateResponse = await fetch(getApiUrl(`/api/patients/${caseId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            image_urls: JSON.stringify(allImageUrls)
          })
        });

        if (updateResponse.ok) {
          setUploadedFiles(allImageUrls);
          console.log('Image URLs saved to PostgreSQL successfully');
        } else {
          console.error('Failed to save image URLs to PostgreSQL');
          console.log('Failed to save image URLs to database');
        }
      } else {
        setUploadedFiles(prev => [...prev, ...newUploadedFiles]);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      console.log(`Failed to upload files: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      // Reset the input
      event.target.value = '';
    }
  };

  const handleProfilePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('handleProfilePhotoUpload called', event.target.files);
    const file = event.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      console.log('Invalid file type:', file.type);
      showAlert(t('patientDetail.invalidImageFile') || 'Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      console.log('File too large:', file.size);
      showAlert(t('patientDetail.fileTooLarge') || 'File size must be less than 5MB');
      return;
    }

    if (!caseId) {
      console.log('No caseId available');
      return;
    }

    console.log('Starting profile photo upload...');
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log('Uploading to:', getApiUrl('/upload'));
      const response = await fetch(getApiUrl('/upload'), {
        method: 'POST',
        body: formData,
      });

      console.log('Upload response status:', response.status);
      if (response.ok) {
        const result = await response.json();
        console.log('Upload result:', result);
        const imageUrl = result.file_url;

        // Update patient data with profile photo URL - include existing patient data
        const updateData: any = {
          profile_photo_url: imageUrl
        };
        
        // Include existing patient data to ensure we don't lose any fields
        if (patientPII) {
          updateData.first_name = patientPII.first_name || '';
          updateData.last_name = patientPII.last_name || '';
          updateData.date_of_birth = patientPII.date_of_birth || '';
          updateData.government_id = patientPII.government_id || '';
          updateData.email = patientPII.email || '';
          updateData.phone = patientPII.phone || '';
          updateData.address = patientPII.address || '';
        }
        
        const updateResponse = await fetch(getApiUrl(`/api/patients/${caseId}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        });

        console.log('Update response status:', updateResponse.status);
        if (updateResponse.ok) {
          // Update local state
          setPatientPII((prev: any) => ({
            ...prev,
            profile_photo_url: imageUrl
          }));
          if (editedPatientData) {
            setEditedPatientData((prev: any) => ({
              ...prev,
              profile_photo_url: imageUrl
            }));
          }
          console.log('Profile photo uploaded and saved successfully');
          showAlert(t('patientDetail.profilePhotoUploaded') || 'Profile photo uploaded successfully');
        } else {
          const errorText = await updateResponse.text();
          console.error('Failed to save profile photo URL. Status:', updateResponse.status, 'Error:', errorText);
          showAlert(t('patientDetail.failedToSaveProfilePhoto') || 'Failed to save profile photo');
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      showAlert(t('patientDetail.failedToUploadProfilePhoto') || 'Failed to upload profile photo');
    } finally {
      setIsUploading(false);
      // Reset the input
      event.target.value = '';
    }
  };

  const handleAddTask = async () => {
    if (!taskForm.title.trim() || !caseId || !user) {
      return;
    }

    setIsSavingTask(true);
    try {
      const result = await createTask({
        title: taskForm.title,
        description: taskForm.description,
        priority: taskForm.priority,
        dueDate: taskForm.dueDate,
        caseId: caseId,
        patientName: patientPII ? `${patientPII.first_name} ${patientPII.last_name}` : undefined
      }, user.email || user.id);

      if (result.success) {
        setShowAddTaskModal(false);
        setTaskForm({
          title: '',
          description: '',
          priority: 'medium',
          dueDate: new Date().toISOString().split('T')[0]
        });
        // Reload tasks
        await loadTasks();
        showAlert(t('patientDetail.taskCreated') || 'Task created successfully');
      } else {
        showAlert(t('patientDetail.taskCreationFailed') || 'Failed to create task');
      }
    } catch (error) {
      console.error('Error creating task:', error);
      showAlert(t('patientDetail.taskCreationFailed') || 'Failed to create task');
    } finally {
      setIsSavingTask(false);
    }
  };

  const handleRemoveProfilePhoto = async () => {
    if (!caseId) return;

    try {
      // Update patient data to remove profile photo URL - include existing patient data
      const updateData: any = {
        profile_photo_url: ''
      };
      
      // Include existing patient data to ensure we don't lose any fields
      if (patientPII) {
        updateData.first_name = patientPII.first_name || '';
        updateData.last_name = patientPII.last_name || '';
        updateData.date_of_birth = patientPII.date_of_birth || '';
        updateData.government_id = patientPII.government_id || '';
        updateData.email = patientPII.email || '';
        updateData.phone = patientPII.phone || '';
        updateData.address = patientPII.address || '';
      }
      
      const updateResponse = await fetch(getApiUrl(`/api/patients/${caseId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      console.log('Remove profile photo response status:', updateResponse.status);
      if (updateResponse.ok) {
        // Update local state
        setPatientPII((prev: any) => ({
          ...prev,
          profile_photo_url: ''
        }));
        if (editedPatientData) {
          setEditedPatientData((prev: any) => ({
            ...prev,
            profile_photo_url: ''
          }));
        }
        console.log('Profile photo removed successfully');
      } else {
        const errorText = await updateResponse.text();
        console.error('Failed to remove profile photo. Status:', updateResponse.status, 'Error:', errorText);
        showAlert(t('patientDetail.failedToSaveProfilePhoto') || 'Failed to remove profile photo');
      }
    } catch (error) {
      console.error('Error removing profile photo:', error);
      showAlert(t('patientDetail.failedToSaveProfilePhoto') || 'Failed to remove profile photo');
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedPatientData({ ...patientPII });
  };

  

  const handleDownloadPDF = async () => {
    if (!patientPII || !caseId) return;

    try {
      const { db } = await import('../firebase');
      await generatePatientPDF(
        patientPII,
        caseId,
        milestones,
        activities,
        translateValue,
        t,
        db
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('PDF creation error');
    }
  };




  const loadMilestones = async () => {
    if (!caseId) {
      console.log('No caseId provided for loading milestones');
      return;
    }
    
    try {
      console.log('Loading milestones for caseId:', caseId);
      
      const { db } = await import('../firebase');
      const { collection, query, where, getDocs, orderBy } = await import('firebase/firestore');
      
      const milestonesRef = collection(db, 'milestones');
      const q = query(milestonesRef, where('caseId', '==', caseId), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      console.log('Found', querySnapshot.docs.length, 'milestones in Firebase');
      
      const loadedMilestones = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Milestone data:', data);
        return {
          id: doc.id, // Use Firebase document ID instead of data.id
          title: data.title,
          description: data.description,
          progress: data.progress,
          targetDate: data.targetDate || '', // Include targetDate field
          status: data.status || 'new', // Include status field, default to 'new'
          axis: data.axis || 'emotional', // ציר - default to emotional
          successMetric: data.successMetric || '', // מדד הצלחה
          resources: data.resources || '', // משאב רלוונטי
          barriers: data.barriers || '', // חסם פוטנציאלי (legacy)
          internalBarriers: data.internalBarriers || '', // חסמים פנימיים
          externalBarriers: data.externalBarriers || '', // חסמים חיצוניים
          notes: data.notes || '', // הערות/מעקב שיחה
          therapistNotes: data.therapistNotes || '', // הערת מטפל
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
          caseId: data.caseId
        };
      });
      
      console.log('Loaded milestones:', loadedMilestones);
      setMilestones(loadedMilestones);
      
      // Initialize selectedMilestones with completed milestones
      const completedMilestoneIds = loadedMilestones
        .filter(milestone => milestone.status === 'achieved')
        .map(milestone => milestone.id);
      setSelectedMilestones(new Set(completedMilestoneIds));
    } catch (error) {
      console.error('Error loading milestones:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.log(`Failed to load milestones: ${errorMessage}`);
    }
  };

  const loadTasks = async () => {
    if (!caseId) {
      console.log('No caseId provided for loading tasks');
      return;
    }
    
    setIsTasksLoading(true);
    try {
      console.log('Loading tasks for caseId:', caseId);
      
      const { db } = await import('../firebase');
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      const tasksRef = collection(db, 'tasks');
      // Simplified query without orderBy to test if it's an index issue
      const q = query(tasksRef, where('caseId', '==', caseId));
      const querySnapshot = await getDocs(q);
      
      console.log('Found', querySnapshot.docs.length, 'tasks in Firebase');
      console.log('Query used: where(caseId ==', caseId, ')');
      
      // Log all task caseIds to debug
      querySnapshot.docs.forEach(doc => {
        console.log('Task ID:', doc.id, 'caseId:', doc.data().caseId, 'title:', doc.data().title);
      });
      
      const loadedTasks: Task[] = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.title || '',
          description: data.description || '',
          caseId: data.caseId || '',
          priority: data.priority || 'medium',
          status: data.status || 'pending',
          dueDate: data.dueDate || '',
          createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          createdBy: data.createdBy || '',
          updatedAt: data.updatedAt?.toDate?.()?.toISOString(),
          updatedBy: data.updatedBy || '',
          patientName: data.patientName || ''
        };
      });
      
      // Sort in memory instead of in query
      loadedTasks.sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      
      console.log('Loaded tasks:', loadedTasks);
      setTasks(loadedTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.log(`Failed to load tasks: ${errorMessage}`);
    } finally {
      setIsTasksLoading(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!taskId) return;
    
    try {
      const { db } = await import('../firebase');
      const { doc, deleteDoc } = await import('firebase/firestore');
      
      const taskRef = doc(db, 'tasks', taskId);
      await deleteDoc(taskRef);
      
      // Refresh tasks
      await loadTasks();
      
      console.log('Task deleted successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleEditMilestone = (milestone: any) => {
    // Don't allow interaction if patient data is still loading
    if (!patientMetadata) {
      showAlert(t('patientDetail.loadingData') || 'Please wait while patient data is loading...');
      return;
    }
    
    // Check if user has permission to edit milestones
    if (!canEditMilestone(user, patientMetadata, userPrograms, userGroups)) {
      showAlert(t('patientDetail.noPermissionToEditMilestone') || 'You do not have permission to edit milestones');
      return;
    }
    
    setEditingMilestone(milestone);
    setNewMilestone({
      title: milestone.title,
      description: milestone.description,
      progress: milestone.progress,
      targetDate: milestone.targetDate || '',
      status: milestone.status || 'new',
      axis: milestone.axis || 'emotional',
      successMetric: milestone.successMetric || '',
      resources: milestone.resources || '',
      barriers: milestone.barriers || '',
      internalBarriers: milestone.internalBarriers || '',
      externalBarriers: milestone.externalBarriers || '',
      notes: milestone.notes || '',
      therapistNotes: milestone.therapistNotes || ''
    });
    setShowEditMilestoneModal(true);
  };

  const handleUpdateMilestone = async () => {
    if (!editingMilestone || !newMilestone.title.trim()) return;
    
    // Check if user has permission to edit milestones
    if (!canEditMilestone(user, patientMetadata, userPrograms, userGroups)) {
      showAlert(t('patientDetail.noPermissionToEditMilestone') || 'You do not have permission to edit milestones');
      return;
    }
    
    try {
      const { db } = await import('../firebase');
      const { doc, updateDoc } = await import('firebase/firestore');
      
      const milestoneRef = doc(db, 'milestones', editingMilestone.id);
      await updateDoc(milestoneRef, {
        title: newMilestone.title,
        description: newMilestone.description,
        progress: newMilestone.progress,
        targetDate: newMilestone.targetDate,
        status: newMilestone.status,
        axis: newMilestone.axis,
        successMetric: newMilestone.successMetric,
        resources: newMilestone.resources,
        barriers: newMilestone.barriers,
        internalBarriers: newMilestone.internalBarriers || '',
        externalBarriers: newMilestone.externalBarriers || '',
        notes: newMilestone.notes,
        therapistNotes: newMilestone.therapistNotes,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setMilestones(milestones.map(m => 
        m.id === editingMilestone.id 
          ? { ...m, ...newMilestone, updatedAt: new Date().toISOString() }
          : m
      ));
      
      setShowEditMilestoneModal(false);
      setEditingMilestone(null);
      setNewMilestone({ 
        title: '', 
        description: '', 
        progress: 0, 
        targetDate: '', 
        status: 'in_progress',
        axis: 'emotional',
        successMetric: '',
        resources: '',
        barriers: '',
        internalBarriers: '',
        externalBarriers: '',
        notes: '',
        therapistNotes: ''
      });
      
      console.log('Milestone updated successfully');
    } catch (error) {
      console.error('Error updating milestone:', error);
      console.log(`Failed to update milestone: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleAddMilestone = async () => {
    // Check if user has permission to add milestones
    if (!canAddMilestone(user, patientMetadata, userPrograms, userGroups)) {
      showAlert(t('patientDetail.noPermissionToAddMilestone') || 'You do not have permission to add milestones');
      return;
    }
    
    if (newMilestone.title.trim() && caseId) {
      try {
        // Add milestone to Firebase first to get the document ID
        const { db } = await import('../firebase');
        const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
        
        const milestoneData = {
          caseId: caseId,
          title: newMilestone.title,
          description: newMilestone.description,
          progress: newMilestone.progress,
          targetDate: newMilestone.targetDate,
          status: newMilestone.status,
          axis: newMilestone.axis,
          successMetric: newMilestone.successMetric,
          resources: newMilestone.resources,
          barriers: newMilestone.barriers,
          internalBarriers: newMilestone.internalBarriers || '',
          externalBarriers: newMilestone.externalBarriers || '',
          notes: newMilestone.notes,
          therapistNotes: newMilestone.therapistNotes,
          createdAt: serverTimestamp()
        };
        
        console.log('Adding milestone to Firebase:', milestoneData);
        
        const docRef = await addDoc(collection(db, 'milestones'), milestoneData);
        
        console.log('Milestone added with ID:', docRef.id);
        
        // Create local milestone object with the Firebase document ID
        const milestone = {
          id: docRef.id,
          ...milestoneData,
          createdAt: new Date().toISOString()
        };
        
        setMilestones([...milestones, milestone]);
        setNewMilestone({ 
          title: '', 
          description: '', 
          progress: 0, 
          targetDate: '', 
          status: 'in_progress',
          axis: 'emotional',
          successMetric: '',
          resources: '',
          barriers: '',
          internalBarriers: '',
          externalBarriers: '',
          notes: '',
          therapistNotes: ''
        });
        setShowAddMilestoneModal(false);
        
        console.log('Milestone added successfully');
      } catch (error) {
        console.error('Error adding milestone:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.log(`Failed to add milestone: ${errorMessage}`);
      }
    }
  };

  

  // Drag and Drop handlers
  const handleDragStart = (milestone: any) => {
    setDraggedMilestone(milestone);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, newAxis: 'emotional' | 'occupational' | 'rights') => {
    e.preventDefault();
    
    try {
      const milestoneData = JSON.parse(e.dataTransfer.getData('text/plain'));
      
      // Don't allow interaction if patient data is still loading
      if (!patientMetadata) {
        showAlert(t('patientDetail.loadingData') || 'Please wait while patient data is loading...');
        setDraggedMilestone(null);
        return;
      }
      
      // Check permission before allowing the drop
      if (!canEditMilestone(user, patientMetadata, userPrograms, userGroups)) {
        showAlert(t('patientDetail.noPermissionToEditMilestone') || 'You do not have permission to edit milestones');
        setDraggedMilestone(null);
        return;
      }
      
      // Don't do anything if dropped in the same column
      if (milestoneData.axis === newAxis) {
        setDraggedMilestone(null);
        return;
      }

      // Update milestone axis in Firebase
      const { db } = await import('../firebase');
      const { doc, updateDoc } = await import('firebase/firestore');
      
      const milestoneRef = doc(db, 'milestones', milestoneData.id);
      await updateDoc(milestoneRef, {
        axis: newAxis,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setMilestones(milestones.map(m => 
        m.id === milestoneData.id 
          ? { ...m, axis: newAxis, updatedAt: new Date().toISOString() }
          : m
      ));
      
      console.log(`Milestone "${milestoneData.title}" moved to ${newAxis} column`);
    } catch (error) {
      console.error('Error updating milestone axis:', error);
    }
    
    setDraggedMilestone(null);
  };

  
  const handleDeleteMilestone = async (id: string) => {
    // Check if user has permission to delete milestones
    if (!canDeleteMilestone(user, patientMetadata, userPrograms, userGroups)) {
      showAlert(t('patientDetail.noPermissionToDeleteMilestone') || 'You do not have permission to delete milestones');
      return;
    }
    
    // Remove from local state immediately
    setMilestones(milestones.filter(milestone => milestone.id !== id));

    try {
      // Remove from Firebase using the document ID directly
      const { db } = await import('../firebase');
      const { doc, deleteDoc } = await import('firebase/firestore');
      
      const milestoneRef = doc(db, 'milestones', id);
      await deleteDoc(milestoneRef);
      
      // Close the edit modal and clear editing milestone
      setShowEditMilestoneModal(false);
      setEditingMilestone(null);
      
      console.log('Milestone deleted successfully');
    } catch (error) {
      console.error('Error deleting milestone:', error);
      // Revert local state on error
      setMilestones([...milestones]);
      console.log('Failed to delete milestone');
    }
  };

  const handleAddMeeting = async () => {
    if (!newMeeting.description.trim() || !newMeeting.date.trim()) {
      console.log('Please fill in description and date');
      return;
    }

    if (!caseId || !user?.email) {
      console.log('Missing required information');
      return;
    }

    try {
      const { db } = await import('../firebase');
      const { collection, addDoc, updateDoc, doc, serverTimestamp } = await import('firebase/firestore');
      
      const meetingNote = `${newMeeting.description}${newMeeting.notes ? ` - ${newMeeting.notes}` : ''}`;
      
      if (editingMeeting) {
        // Update existing meeting
        await updateDoc(doc(db, 'activities', editingMeeting.id), {
          note: meetingNote,
          meetingDate: newMeeting.date,
          meetingDescription: newMeeting.description,
          meetingNotes: newMeeting.notes,
          updatedAt: serverTimestamp()
        });
      } else {
        // Add new meeting
        await addDoc(collection(db, 'activities'), {
          caseId: caseId,
          note: meetingNote,
          action: 'meeting',
          createdBy: user.email,
          timestamp: serverTimestamp(),
          userEmail: user.email,
          meetingDate: newMeeting.date,
          meetingDescription: newMeeting.description,
          meetingNotes: newMeeting.notes
        });
      }
      
      // Close modal and reset form
      setShowAddMeetingModal(false);
      setNewMeeting({ description: '', date: getTodayDate(), notes: '' });
      setEditingMeeting(null);
      
      // Reload activities to show the changes
      await loadPatientData();
    } catch (error) {
      console.error('Error saving meeting record:', error);
      console.log('Failed to save meeting record. Please try again.');
    }
  };

  const handleSaveEdit = async () => {
    if (!caseId || !editedPatientData) return;
    
    try {
      // Update patient data in PostgreSQL via fileserver
      const response = await fetch(getApiUrl(`/api/patients/${caseId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: editedPatientData.first_name,
          last_name: editedPatientData.last_name,
          date_of_birth: editedPatientData.date_of_birth,
          government_id: editedPatientData.government_id,
          email: editedPatientData.email,
          phone: editedPatientData.phone,
          address: editedPatientData.address,
          profile_photo_url: editedPatientData.profile_photo_url
        })
      });

      if (response.ok) {
        // Save non-PII fields to Firebase
        const { doc, updateDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const patientDocRef = doc(db, 'patients', caseId);
        await updateDoc(patientDocRef, {
          gender: editedPatientData.gender,
          maritalStatus: editedPatientData.marital_status,
          education: editedPatientData.education,
          notes: editedPatientData.notes,
          updatedAt: new Date()
        });
        
        // Update local state only after successful save
        setPatientPII(editedPatientData);
        setIsEditing(false);
        setEditedPatientData(null);
        
        // Add activity note for the update
        if (user) {
          const { addActivityNote } = await import('../firebase');
          await addActivityNote(caseId, 'Patient information updated', 'updated', user.email);
        }
        
        console.log('Patient data updated successfully');
      } else {
        const errorText = await response.text();
        console.error('Failed to update patient data:', errorText);
        console.log('Failed to save patient data');
      }
    } catch (error) {
      console.error('Error saving patient data:', error);
      console.log('Failed to save patient data');
    }
  };

  
  const handleFieldChange = (field: string, value: string) => {
    setEditedPatientData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  
  const handleDeleteMeeting = async (meetingId: string) => {
    try {
      await deleteActivityLog(meetingId);
      
      // Close modal and reset form
      setShowAddMeetingModal(false);
      setNewMeeting({ description: '', date: getTodayDate(), notes: '' });
      setEditingMeeting(null);
      
      // Reload activities to reflect the deletion
      await loadPatientData();
      console.log('Meeting deleted successfully');
    } catch (err: any) {
      console.error('Error deleting meeting:', err);
      showAlert(t('patientDetail.deleteMeeting') + ': ' + (err.message || 'Failed to delete meeting'));
      // Close modal even on error
      setShowAddMeetingModal(false);
      setEditingMeeting(null);
    }
  };

  const handleAddComment = async (meetingId: string) => {
    if (!meetingComment.trim() || !user?.email) {
      showAlert(t('patientDetail.enterComment') || 'Please enter a comment');
      return;
    }

    try {
      const { db } = await import('../firebase');
      const { doc, updateDoc, arrayUnion, serverTimestamp, getDoc, Timestamp } = await import('firebase/firestore');
      
      const meetingRef = doc(db, 'activities', meetingId);
      
      // Check if document exists
      const meetingDoc = await getDoc(meetingRef);
      if (!meetingDoc.exists()) {
        throw new Error('Activity document not found');
      }
      
      // Use Timestamp.now() instead of serverTimestamp() for array elements
      // serverTimestamp() doesn't work well inside arrayUnion objects
      const commentData = {
        comment: meetingComment.trim(),
        createdBy: user.email,
        createdAt: Timestamp.now(),
        userName: user.name || user.email
      };
      
      // Use arrayUnion which will create the field if it doesn't exist
      await updateDoc(meetingRef, {
        managerComments: arrayUnion(commentData),
        updatedAt: serverTimestamp()
      });
      
      // Reset comment state
      setMeetingComment('');
      setCommentingMeetingId(null);
      
      // Reload activities to show the new comment
      await loadPatientData();
      showAlert(t('patientDetail.commentAdded') || 'Comment added successfully');
    } catch (error: any) {
      console.error('Error adding comment:', error);
      const errorMessage = error?.message || 'Unknown error';
      console.error('Full error details:', errorMessage);
      showAlert(t('patientDetail.commentError') || `Failed to add comment. Please try again. Error: ${errorMessage}`);
    }
  };

  if (isLoading) {
    return (
      <div className="app">
        <div className="container">
          <div className="login-card">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '18px', color: '#666666' }}>
                {i18n.language === 'he' ? 'טוען...' : 'Loading...'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user && !isLoading) {
    console.log('No user found and not loading, redirecting to login');
    return <Navigate to="/" replace />;
  }

  // Don't render if user is not loaded
  if (!user) {
    return null;
  }

  return (
    <>
    <div className="app">
        <MobileHeader
          user={user}
          isMobileMenuOpen={isMobileMenuOpen}
          onToggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        />

        <div className="dashboard-container">
          <div className="dashboard">
            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
              <div 
                className="mobile-menu-overlay"
                onClick={() => setIsMobileMenuOpen(false)}
              ></div>
            )}
            
            <Sidebar
              user={user}
              activeTab="projects"
              isMobileMenuOpen={isMobileMenuOpen}
              onTabChange={handleTabChange}
              onSignOut={() => showConfirm(t('navigation.confirmSignOut'), () => navigate('/'))}
              onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
            />

            <div className="main-content">
              <div className="login-header" style={{ padding: '0' }}>
                    <div className="header-top-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                      <div className="header-left" style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' }}>
                          {caseId}
                          {patientPII && patientPII.first_name && patientPII.last_name && (
                            <span style={{ marginLeft: '12px', fontWeight: '400', color: '#666' }}>
                              - {patientPII.first_name} {patientPII.last_name}
                            </span>
                          )}
                        </p>
                      </div>
                      {/* Assigned Users Section - Centered */}
                      {patientMetadata && Array.isArray(patientMetadata.assignedAdmins) && patientMetadata.assignedAdmins.length > 0 && (
                        <div style={{ 
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          display: 'flex', 
                          alignItems: 'center',
                          gap: '8px',
                          flexWrap: 'wrap',
                          flexDirection: i18n.language === 'he' ? 'row-reverse' : 'row'
                        }}>
                          <span style={{ fontSize: '12px', color: '#666', marginRight: i18n.language === 'he' ? '0' : '4px', marginLeft: i18n.language === 'he' ? '4px' : '0' }}>
                            {t('patientDetail.assignedUsers') || 'Assigned'}
                          </span>
                          {patientMetadata.assignedAdmins.map((userId: string) => {
                            const canRemove = isSuperAdmin(user) || 
                              (userPrograms && userPrograms.length > 0) || 
                              (userGroups && userGroups.length > 0);
                            
                            return (
                              <AssignedUserAvatar
                                key={userId}
                                userId={userId}
                                canRemove={canRemove}
                                onClick={canRemove ? (e: React.MouseEvent) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  
                                  if (!caseId) return;
                                  
                                  // Show confirmation dialog
                                  showConfirm(
                                    t('patientDetail.confirmRemoveAssignment') || 'Are you sure you want to unassign this user?',
                                    async () => {
                                      try {
                                        const updatedAdmins = patientMetadata.assignedAdmins.filter((id: string) => id !== userId);
                                        const result = await updatePatientAssignedAdmins(caseId, updatedAdmins);
                                        
                                        if (result.success) {
                                          // Update local state
                                          setPatientMetadata({
                                            ...patientMetadata,
                                            assignedAdmins: updatedAdmins
                                          });
                                          
                                          // Add activity note
                                          if (user) {
                                            const { addActivityNote } = await import('../firebase');
                                            await addActivityNote(
                                              caseId,
                                              `User assignment removed: ${userId}`,
                                              'updated',
                                              user.email
                                            );
                                          }
                                          
                                          showAlert(t('patientDetail.assignmentRemoved') || 'Assignment removed successfully');
                                          await loadPatientData();
                                        } else {
                                          showAlert(t('patientDetail.failedToRemoveAssignment') || 'Failed to remove assignment');
                                        }
                                      } catch (error) {
                                        console.error('Error removing assignment:', error);
                                        showAlert(t('patientDetail.failedToRemoveAssignment') || 'Failed to remove assignment');
                                      }
                                    }
                                  );
                                } : undefined}
                              />
                            );
                          })}
                        </div>
                      )}
                      <div className="header-right">
                        {canGenerateReports(user) && (
                        <button
                          onClick={handleDownloadPDF}
                          style={{
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#218838';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#28a745';
                          }}
                        >
                          {i18n.language === 'he' ? 'הפקת דוחות' : 'Generate Reports'}
                        </button>
                        )}
                      </div>
                    </div>
              </div>

          {patientPII ? (
            <div style={{ padding: '0' }}>
              {/* AI Summary Block */}
              {caseId && (
                <AITabSummary 
                  caseId={caseId}
                  tabName="progress"
                  patientPII={patientPII}
                />
              )}

              {/* Tab Navigation */}
              <div className="patient-detail-tabs" style={{ width: '100%', maxWidth: '100%', height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
                <div className="tab-navigation">
                  <button
                    className={`tab-button ${activeDetailTab === 'progress' ? 'active' : ''}`}
                    onClick={() => {
                      console.log('Switching to progress tab');
                      setActiveDetailTab('progress');
                    }}
                  >
                    {t('patientDetail.progress')}
                  </button>
                  <button
                    className={`tab-button ${activeDetailTab === 'activity' ? 'active' : ''}`}
                    onClick={() => {
                      console.log('Switching to activity tab');
                      setActiveDetailTab('activity');
                    }}
                  >
                    {t('patientDetail.meetings')}
                  </button>
                  <button
                    className={`tab-button ${activeDetailTab === 'notes' ? 'active' : ''}`}
                    onClick={() => {
                      console.log('Switching to notes tab');
                      setActiveDetailTab('notes');
                    }}
                  >
                    {t('patientDetail.intakeForm')}
                  </button>
                  <button
                    className={`tab-button ${activeDetailTab === 'patient-info' ? 'active' : ''}`}
                    onClick={() => {
                      console.log('Switching to patient-info tab');
                      setActiveDetailTab('patient-info');
                    }}
                  >
                    {t('patientDetail.information')}
                  </button>
                  <button
                    className={`tab-button ${activeDetailTab === 'tasks' ? 'active' : ''}`}
                    onClick={() => {
                      console.log('Switching to tasks tab');
                      setActiveDetailTab('tasks');
                      loadTasks(); // Reload tasks when switching to tasks tab
                    }}
                  >
                    {t('patientDetail.tasks')}
                  </button>
                  {/* Only show Documents tab if user has permission to manage documents */}
                  {canManageDocuments(user, patientMetadata, userPrograms, userGroups) && (
                    <button
                      className={`tab-button ${activeDetailTab === 'documents' ? 'active' : ''}`}
                      onClick={() => {
                        console.log('Switching to documents tab');
                        setActiveDetailTab('documents');
                      }}
                    >
                      {t('patientDetail.documents')}
                    </button>
                  )}
                </div>

                {/* Tab Content */}
                <div className="tab-content" style={{ flex: 1, overflow: 'auto', padding: '0', boxSizing: 'border-box' }}>
                  <div style={{ padding: caseId ? '20px' : '0' }}>
                  {activeDetailTab === 'documents' && (
                    <DocumentsSection 
                      uploadedFiles={uploadedFiles}
                      isUploading={isUploading}
                      handleFileUpload={handleFileUpload}
                      handleDeleteFile={handleDeleteFile}
                      canManage={canManageDocuments(user, patientMetadata, userPrograms, userGroups)}
                    />
                  )}

                  {activeDetailTab === 'activity' && (
                    <div className="tab-panel" style={{ position: 'relative', padding: '0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <h3 className="form-block-title" style={{ color: '#000000', margin: 0 }}>📊 {t('patientDetail.meetings')}</h3>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          <select
                            value={meetingFilter}
                            onChange={(e) => setMeetingFilter(e.target.value as 'active' | 'archived')}
                            style={{
                              padding: '8px 12px',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              fontSize: '14px',
                              backgroundColor: 'white',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="active">{t('patientDetail.filterActive')}</option>
                            <option value="archived">{t('patientDetail.filterArchived')}</option>
                          </select>
                          <button
                            onClick={openAddMeetingModal}
                            style={{
                              padding: '10px 20px',
                              backgroundColor: '#007acc',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500',
                              transition: 'background 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#005a9e';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#007acc';
                            }}
                          >
                            + {t('patientDetail.addMeeting')}
                          </button>
                        </div>
                      </div>
                      {activities.filter(activity => activity.action === 'meeting').length > 0 ? (
                        <div className="activity-list">
                          {activities
                            .filter(activity => activity.action === 'meeting')
                            .filter(activity => {
                              if (meetingFilter === 'active') return !activity.archived;
                              if (meetingFilter === 'archived') return activity.archived;
                              return false;
                            })
                            .sort((a, b) => {
                              // Sort by timestamp in descending order (most recent first)
                              const dateA = new Date(a.timestamp);
                              const dateB = new Date(b.timestamp);
                              return dateB.getTime() - dateA.getTime();
                            })
                            .map((activity) => {
                              const canEdit = canEditMeeting(user, activity);
                              return (
                            <div 
                              key={activity.id} 
                              className="activity-item"
                              onClick={() => canEdit && openEditMeetingModal(activity)}
                              style={{ 
                                cursor: canEdit ? 'pointer' : 'default',
                                opacity: activity.archived ? 0.7 : 1,
                                backgroundColor: activity.archived ? '#f5f5f5' : '#ffffff',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                padding: '16px',
                                marginBottom: '12px',
                                position: 'relative',
                                transition: 'all 0.2s ease',
                                boxShadow: activity.archived ? 'none' : '0 1px 3px rgba(0,0,0,0.08)'
                              }}
                              onMouseEnter={(e) => {
                                if (canEdit) {
                                  e.currentTarget.style.backgroundColor = '#fafafa';
                                  e.currentTarget.style.borderColor = '#d0d0d0';
                                  e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.12)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = activity.archived ? '#f5f5f5' : '#ffffff';
                                e.currentTarget.style.borderColor = '#e0e0e0';
                                e.currentTarget.style.boxShadow = activity.archived ? 'none' : '0 1px 3px rgba(0,0,0,0.08)';
                              }}
                            >
                              <div className="activity-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e8e8e8' }}>
                                <span className="activity-action" style={{ flex: 1, fontSize: '14px', fontWeight: '600', color: '#2c3e50', textTransform: 'uppercase', letterSpacing: '0.5px', background: 'transparent', padding: '0' }}>
                                  {activity.archived ? '📦 ' : ''}{t(`actions.${activity.action}`) || activity.action}
                                </span>
                                <div className="activity-header-right">
                                  <span className="activity-time" style={{ fontSize: '13px', color: '#7f8c8d', fontWeight: '500' }}>{formatDate(activity.timestamp)}</span>
                                </div>
                              </div>
                              <div className="activity-note" style={{ width: '100%', padding: '0 0 12px 0', fontSize: '15px', lineHeight: '1.6', color: '#34495e' }}>{activity.note.replace(/^Meeting: /, '')}</div>
                              
                              {/* Manager Comments Section */}
                              {activity.managerComments && Array.isArray(activity.managerComments) && activity.managerComments.length > 0 && (() => {
                                const comments = activity.managerComments;
                                return (
                                  <div style={{ marginTop: '12px', marginBottom: '12px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px', borderLeft: '3px solid #007acc' }}>
                                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#007acc', marginBottom: '8px' }}>
                                      {t('patientDetail.managerComments') || 'Manager Comments:'}
                                    </div>
                                    {comments!.map((comment: any, idx: number) => (
                                      <div key={idx} style={{ marginBottom: idx < comments!.length - 1 ? '10px' : '0', paddingBottom: idx < comments!.length - 1 ? '10px' : '0', borderBottom: idx < comments!.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                                        <div style={{ fontSize: '14px', color: '#34495e', marginBottom: '4px' }}>{comment.comment}</div>
                                        <div style={{ fontSize: '11px', color: '#7f8c8d' }}>
                                          👤 {comment.userName || comment.createdBy} - {comment.createdAt ? formatDate(comment.createdAt) : ''}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                              
                              {/* Add Comment Section (only for managers) */}
                              {canCommentOnMeeting(user) && (
                                <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                                  {commentingMeetingId === activity.id ? (
                                    <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                                      <textarea
                                        value={meetingComment}
                                        onChange={(e) => setMeetingComment(e.target.value)}
                                        placeholder={t('patientDetail.enterComment') || 'Enter your comment...'}
                                        rows={3}
                                        style={{
                                          width: '100%',
                                          padding: '8px',
                                          border: '1px solid #ced4da',
                                          borderRadius: '4px',
                                          fontSize: '14px',
                                          resize: 'vertical',
                                          marginBottom: '8px'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCommentingMeetingId(null);
                                            setMeetingComment('');
                                          }}
                                          style={{
                                            background: '#6c757d',
                                            color: 'white',
                                            border: 'none',
                                            padding: '6px 12px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '13px'
                                          }}
                                        >
                                          {t('common.cancel') || 'Cancel'}
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddComment(activity.id);
                                          }}
                                          style={{
                                            background: '#007acc',
                                            color: 'white',
                                            border: 'none',
                                            padding: '6px 12px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '13px'
                                          }}
                                        >
                                          {t('patientDetail.addComment') || 'Add Comment'}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setCommentingMeetingId(activity.id);
                                      }}
                                      style={{
                                        background: 'transparent',
                                        color: '#007acc',
                                        border: '1px solid #007acc',
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        width: '100%'
                                      }}
                                    >
                                      💬 {t('patientDetail.addComment') || 'Add Comment'}
                                    </button>
                                  )}
                                </div>
                              )}
                              
                              <div className="activity-author" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: '#7f8c8d' }}>
                                <span style={{ fontWeight: '500' }}>👤 {activity.userEmail || activity.createdBy || t('unknownUser')}</span>
                                {!canEdit && (
                                  <span style={{ fontSize: '11px', color: '#999', fontStyle: 'italic' }}>
                                    🔒 {t('patientDetail.readOnly') || 'Read only'}
                                  </span>
                                )}
                            </div>
                            </div>
                          );
                          })}
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                          <h4 style={{ color: '#000000', marginBottom: '8px' }}>{t('patientDetail.noMeetingsYet')}</h4>
                          <p>{t('patientDetail.addFirstMeeting')}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeDetailTab === 'patient-info' && (
                    <PatientInformationSection 
                      patientPII={patientPII}
                      isEditing={isEditing}
                      editedPatientData={editedPatientData}
                      handleEditClick={handleEditClick}
                      handleSaveEdit={handleSaveEdit}
                      handleFieldChange={handleFieldChange}
                      translateValue={translateValue}
                      handleProfilePhotoUpload={handleProfilePhotoUpload}
                      handleRemoveProfilePhoto={handleRemoveProfilePhoto}
                      isUploading={isUploading}
                    />
                  )}

                  {activeDetailTab === 'notes' && caseId && (
                    <IntakeFormsSection 
                      caseId={caseId} 
                      intakeStatus={intakeStatus}
                      userRole={user?.role}
                      user={user}
                    />
                  )}

                  {activeDetailTab === 'progress' && (
                    <ProgressSection
                      milestones={milestones}
                      user={user}
                      patientMetadata={patientMetadata}
                      userPrograms={userPrograms}
                      userGroups={userGroups}
                      canAddMilestone={canAddMilestone}
                      canEditMilestone={canEditMilestone}
                      setShowAddMilestoneModal={setShowAddMilestoneModal}
                      handleEditMilestone={handleEditMilestone}
                      handleDragStart={handleDragStart}
                      handleDragOver={handleDragOver}
                      handleDrop={handleDrop}
                      draggedMilestone={draggedMilestone}
                      handleMilestoneSelect={handleMilestoneSelect}
                    />
                  )}

                  {activeDetailTab === 'tasks' && (
                    <div className="tab-panel" style={{ position: 'relative', padding: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 className="form-block-title" style={{ color: '#000000', margin: 0 }}>
                          📋 {t('patientDetail.tasks')}
                        </h3>
                        <button
                          onClick={() => setShowAddTaskModal(true)}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#007acc',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#005a9e';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#007acc';
                          }}
                        >
                          + {t('patientDetail.addTask') || 'Add Task'}
                        </button>
                      </div>
                      {isTasksLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          {t('common.loading') || 'Loading...'}
                        </div>
                      ) : tasks.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {tasks.map((task) => (
                            <div
                              key={task.id}
                              style={{
                                padding: '16px',
                                background: task.status === 'completed' ? '#e8f5e9' : task.status === 'overdue' ? '#ffebee' : '#fff3e0',
                                borderRadius: '8px',
                                borderLeft: i18n.language === 'he' ? 'none' : `4px solid ${task.status === 'completed' ? '#4caf50' : task.status === 'overdue' ? '#f44336' : '#ff9800'}`,
                                borderRight: i18n.language === 'he' ? `4px solid ${task.status === 'completed' ? '#4caf50' : task.status === 'overdue' ? '#f44336' : '#ff9800'}` : 'none',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                position: 'relative'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: '600', fontSize: '16px', color: '#333', marginBottom: '8px' }}>
                                    {task.title}
                                  </div>
                                  {task.description && (
                                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                                      {task.description}
                                    </div>
                                  )}
                                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '12px', color: '#888' }}>
                                    {task.dueDate && (
                                      <span>⏰ {formatDate(task.dueDate)}</span>
                                    )}
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      backgroundColor: task.priority === 'urgent' ? '#f44336' : task.priority === 'high' ? '#ff9800' : task.priority === 'medium' ? '#ffc107' : '#4caf50',
                                      color: 'white',
                                      fontSize: '11px',
                                      fontWeight: '500'
                                    }}>
                                      {task.priority === 'urgent' ? (i18n.language === 'he' ? 'דחוף' : 'Urgent') : task.priority === 'high' ? (i18n.language === 'he' ? 'גבוה' : 'High') : task.priority === 'medium' ? (i18n.language === 'he' ? 'בינוני' : 'Medium') : (i18n.language === 'he' ? 'נמוך' : 'Low')}
                                    </span>
                                    <span style={{
                                      padding: '2px 8px',
                                      borderRadius: '12px',
                                      backgroundColor: task.status === 'completed' ? '#4caf50' : task.status === 'overdue' ? '#f44336' : '#2196f3',
                                      color: 'white',
                                      fontSize: '11px',
                                      fontWeight: '500'
                                    }}>
                                      {task.status === 'completed' ? (i18n.language === 'he' ? 'הושלם' : 'Completed') : task.status === 'overdue' ? (i18n.language === 'he' ? 'איחור' : 'Overdue') : task.status === 'inProgress' ? (i18n.language === 'he' ? 'בביצוע' : 'In Progress') : (i18n.language === 'he' ? 'ממתין' : 'Pending')}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteTask(task.id);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: '8px',
                                    [i18n.language === 'he' ? 'left' : 'right']: '8px',
                                    background: 'rgba(220, 53, 69, 0.1)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '28px',
                                    height: '28px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    color: '#dc3545',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s ease',
                                    padding: 0,
                                    lineHeight: 1
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#dc3545';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(220, 53, 69, 0.1)';
                                    e.currentTarget.style.color = '#dc3545';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                  title={t('common.delete') || 'Delete'}
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                          <h4 style={{ color: '#000000', marginBottom: '8px' }}>{t('patientDetail.noTasksYet')}</h4>
                          <p>{t('patientDetail.addFirstTask')}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Add Task Modal */}
                  {showAddTaskModal && createPortal(
                    <div style={{
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10000
                    }}
                    onClick={() => setShowAddTaskModal(false)}
                    >
                      <div 
                        style={{
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          padding: '24px',
                          width: '90%',
                          maxWidth: '500px',
                          maxHeight: '90vh',
                          overflow: 'auto',
                          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                          ...(i18n.language === 'he' ? { direction: 'rtl' } : { direction: 'ltr' })
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                            {t('patientDetail.addTask') || 'Add Task'}
                          </h2>
                          <button
                            onClick={() => setShowAddTaskModal(false)}
                            style={{
                              background: 'none',
                              border: 'none',
                              fontSize: '24px',
                              cursor: 'pointer',
                              color: '#666',
                              padding: 0,
                              width: '30px',
                              height: '30px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ×
                          </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                              {t('dashboard.taskTitle') || 'Task Title'} *
                            </label>
                            <input
                              type="text"
                              value={taskForm.title}
                              onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                              placeholder={t('dashboard.enterTaskTitle') || 'Enter task title'}
                              style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                              autoFocus
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                              {t('dashboard.taskDescription') || 'Description'}
                            </label>
                            <textarea
                              value={taskForm.description}
                              onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                              placeholder={t('dashboard.enterTaskDescription') || 'Enter task description'}
                              rows={4}
                              style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '14px',
                                resize: 'vertical'
                              }}
                            />
                          </div>

                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                              {t('dashboard.priority') || 'Priority'}
                            </label>
                            <select
                              value={taskForm.priority}
                              onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value as 'low' | 'medium' | 'high' | 'urgent' })}
                              style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            >
                              <option value="low">{i18n.language === 'he' ? 'נמוך' : 'Low'}</option>
                              <option value="medium">{i18n.language === 'he' ? 'בינוני' : 'Medium'}</option>
                              <option value="high">{i18n.language === 'he' ? 'גבוה' : 'High'}</option>
                              <option value="urgent">{i18n.language === 'he' ? 'דחוף' : 'Urgent'}</option>
                            </select>
                          </div>

                          <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                              {t('dashboard.dueDate') || 'Due Date'}
                            </label>
                            <input
                              type="date"
                              value={taskForm.dueDate}
                              onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                              style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                          </div>

                          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                            <button
                              onClick={() => setShowAddTaskModal(false)}
                              style={{
                                padding: '10px 20px',
                                backgroundColor: '#f0f0f0',
                                color: '#333',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                            >
                              {t('patientDetail.cancel') || 'Cancel'}
                            </button>
                            <button
                              onClick={handleAddTask}
                              disabled={!taskForm.title.trim() || isSavingTask}
                              style={{
                                padding: '10px 20px',
                                backgroundColor: !taskForm.title.trim() || isSavingTask ? '#ccc' : '#007acc',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: !taskForm.title.trim() || isSavingTask ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                              }}
                            >
                              {isSavingTask ? (t('patientDetail.saving') || 'Saving...') : (t('patientDetail.addTask') || 'Add Task')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}
                  </div>

                </div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: '18px', color: '#666666' }}>
                {t('patientDetail.noPatientData')}
              </div>
            </div>
          )}
            </div>
          </div>
        </div>

        {/* Meeting Modal */}
        <MeetingModal
          show={showAddMeetingModal}
          editingMeeting={editingMeeting}
          newMeeting={newMeeting}
          onClose={() => {
            setShowAddMeetingModal(false);
            setEditingMeeting(null);
            setNewMeeting({ description: '', date: getTodayDate(), notes: '' });
          }}
          onSave={handleAddMeeting}
          onDelete={handleDeleteMeeting}
          onChange={setNewMeeting}
          getTodayDate={getTodayDate}
          showConfirm={showConfirm}
        />

        {/* Milestone Modals */}
        <MilestoneModals
          showAddModal={showAddMilestoneModal}
          showEditModal={showEditMilestoneModal}
          editingMilestone={editingMilestone}
          newMilestone={newMilestone}
          onCloseAdd={() => {
            setShowAddMilestoneModal(false);
            setNewMilestone({ 
              title: '', 
              description: '', 
              progress: 0, 
              targetDate: '', 
              status: 'in_progress',
              axis: 'emotional',
              successMetric: '',
              resources: '',
              barriers: '',
              internalBarriers: '',
              externalBarriers: '',
              notes: '',
              therapistNotes: ''
            });
          }}
          onCloseEdit={() => {
            setShowEditMilestoneModal(false);
            setEditingMilestone(null);
            setNewMilestone({ 
              title: '', 
              description: '', 
              progress: 0, 
              targetDate: '', 
              status: 'in_progress',
              axis: 'emotional',
              successMetric: '',
              resources: '',
              barriers: '',
              internalBarriers: '',
              externalBarriers: '',
              notes: '',
              therapistNotes: ''
            });
          }}
          onAdd={handleAddMilestone}
          onUpdate={handleUpdateMilestone}
          onDelete={handleDeleteMilestone}
          onChange={(milestone) => setNewMilestone({
            ...milestone,
            internalBarriers: milestone.internalBarriers || '',
            externalBarriers: milestone.externalBarriers || ''
          })}
          showConfirm={showConfirm}
          user={user || undefined}
        />
        <DialogComponent />
        {caseId && <AIChatButton caseId={caseId} />}
    </div>
    </>
  );
}
